import { Mail, RotateCcw, Store, Clock, ListOrdered, AlertTriangle } from 'lucide-react';

const Block = ({ icon: Icon, title, children, testId }) => (
  <div style={{ marginTop: 22 }} data-testid={testId}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <Icon size={15} style={{ opacity: 0.6 }} />
      <strong style={{ fontSize: 14 }}>{title}</strong>
    </div>
    <div style={{ fontSize: 13.5, lineHeight: 1.75, whiteSpace: 'pre-line', opacity: 0.85 }}>
      {children}
    </div>
  </div>
);

/**
 * A store's full return policy, as a shopper reads it. Fed by
 * GET /api/marketplace/return-policy/{slug or vendor id}.
 */
export function ReturnPolicyView({ data }) {
  if (!data) return null;
  const p = data.policy || {};

  const terms = p.accepts_returns
    ? [
        ['Window', `${p.window_days} days from delivery`],
        ['You receive', p.resolution === 'replace' ? 'A replacement'
          : p.resolution === 'either' ? 'A refund or a replacement — your choice' : 'A refund'],
        ['Return shipping', p.return_shipping_paid_by === 'vendor' ? 'Paid by the store' : 'Paid by you'],
        ...(p.restocking_fee_percent > 0 ? [['Restocking fee', `${p.restocking_fee_percent}%`]] : []),
      ]
    : [];

  return (
    <div data-testid="return-policy-view">
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <RotateCcw size={18} style={{ opacity: 0.65 }} />
        <h2 style={{ fontSize: 18, margin: 0 }}>Returns &amp; refunds</h2>
      </div>

      <p style={{ fontSize: 13.5, lineHeight: 1.7, marginTop: 10, opacity: 0.85 }} data-testid="return-policy-headline">
        {p.accepts_returns
          ? `${data.store_name} accepts returns within ${p.window_days} days of delivery.`
          : `${data.store_name} does not accept returns.`}
      </p>

      {terms.length > 0 && (
        <div
          data-testid="return-policy-terms"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12, marginTop: 16,
          }}
        >
          {terms.map(([k, v]) => (
            <div key={k} style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.5 }}>{k}</div>
              <div style={{ fontSize: 13.5, marginTop: 3 }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {(p.excludes_custom || p.excludes_digital || p.non_returnable_note || data.consumable_note) && (
        <Block icon={AlertTriangle} title="What can't be returned" testId="return-policy-exclusions">
          {[
            p.excludes_custom && 'Made-to-order and personalised items are final sale.',
            p.excludes_digital && 'Digital downloads are final sale once delivered.',
            data.consumable_note,
            p.non_returnable_note,
          ].filter(Boolean).join('\n')}
          {'\nIndividual items may also be marked final sale — the product page will say so.'}
        </Block>
      )}

      {p.process_steps && (
        <Block icon={ListOrdered} title="How to start a return" testId="return-policy-process">
          {p.process_steps}
        </Block>
      )}

      {p.refund_timeframe && (
        <Block icon={Clock} title="How long refunds and exchanges take" testId="return-policy-timeframe">
          {p.refund_timeframe}
        </Block>
      )}

      {p.exceptions_note && (
        <Block icon={AlertTriangle} title="Damaged, defective or incorrect items" testId="return-policy-exceptions">
          {p.exceptions_note}
        </Block>
      )}

      {p.returns_email && (
        <Block icon={Mail} title="Who to contact" testId="return-policy-contact">
          <a href={`mailto:${p.returns_email}`} style={{ textDecoration: 'underline' }}>{p.returns_email}</a>
        </Block>
      )}

      <div
        data-testid="return-policy-handling"
        style={{
          display: 'flex', gap: 10, marginTop: 26, padding: '14px 16px', borderRadius: 12,
          background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.07)',
          fontSize: 13, lineHeight: 1.7,
        }}
      >
        <Store size={16} style={{ marginTop: 2, flexShrink: 0, opacity: 0.7 }} />
        <span>{data.handling_note}</span>
      </div>
    </div>
  );
}

export default ReturnPolicyView;
